import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Send, Save, RefreshCw, Loader2, AlertCircle, ShieldCheck, KeyRound, AtSign } from 'lucide-react';
import { formatApiErrorDetail } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const EmailSettingsPanel = () => {
  const [config, setConfig] = useState(null);
  const [senderInput, setSenderInput] = useState('');
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [testTo, setTestTo] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await axios.get(`${API}/admin/email-settings`);
      setConfig(res.data);
      setSenderInput(res.data.sender_email || '');
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true); setError('');
    try {
      const payload = {};
      if (senderInput !== (config?.sender_email || '')) payload.sender_email = senderInput.trim();
      if (apiKeyInput.trim()) payload.resend_api_key = apiKeyInput.trim();
      if (Object.keys(payload).length === 0) {
        setToast('Nothing changed.');
        setTimeout(() => setToast(''), 2000);
        setSaving(false);
        return;
      }
      const res = await axios.put(`${API}/admin/email-settings`, payload);
      setConfig(res.data);
      setApiKeyInput('');
      setToast('Email settings saved. New approval emails use these values immediately.');
      setTimeout(() => setToast(''), 3500);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    if (!testTo.includes('@')) {
      setError('Enter a valid recipient email for the test send.');
      return;
    }
    setTesting(true); setError('');
    try {
      const res = await axios.post(`${API}/admin/email-settings/test`, { to: testTo.trim() });
      if (res.data?.sent) {
        setToast(`Test email delivered to ${testTo} (provider_id=${(res.data.provider_id || '').slice(0, 8)}…).`);
      } else {
        setError(`Resend rejected: ${res.data?.last_error || 'unknown error'}`);
      }
      setTimeout(() => setToast(''), 4000);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <section className="glass-card rounded-2xl border border-slate-800 p-6 text-slate-500 font-mono text-xs flex items-center gap-2" data-testid="admin-email-settings-loading">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading email settings…
      </section>
    );
  }

  const sourceBadge = (active) => (
    <span className={`font-mono text-[8px] uppercase tracking-widest px-1.5 py-0.5 rounded ${active ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/40' : 'bg-slate-800 text-slate-500 border border-slate-700'}`}>
      {active ? 'in use' : 'fallback'}
    </span>
  );

  return (
    <section className="glass-card rounded-2xl border border-slate-800 p-6 space-y-5" data-testid="admin-email-settings">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Send className="w-4 h-4 text-[#39ff14]" />
          <h3 className="font-display font-bold text-sm text-white uppercase tracking-wide">Email Pipeline Settings</h3>
        </div>
        <button
          onClick={load}
          className="px-2.5 py-1 rounded border border-[#39ff14]/40 bg-[#39ff14]/10 text-[#39ff14] hover:bg-[#39ff14]/20 font-mono text-[10px] uppercase tracking-widest font-bold flex items-center gap-1.5 transition-all cursor-pointer"
          data-testid="btn-email-settings-refresh"
        >
          <RefreshCw className="w-3 h-3" /><span>Reload</span>
        </button>
      </div>

      {toast && (
        <div className="rounded border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 px-3 py-2 text-[11px] font-mono" data-testid="email-settings-toast">
          {toast}
        </div>
      )}
      {error && (
        <div className="rounded border border-rose-500/30 bg-rose-500/10 text-rose-300 px-3 py-2 text-xs font-mono flex items-start gap-2" data-testid="email-settings-error">
          <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> <span className="break-words">{error}</span>
        </div>
      )}

      <div className="space-y-3">
        <div>
          <label className="font-mono text-[10px] uppercase tracking-widest text-slate-400 flex items-center gap-1.5 mb-1.5">
            <AtSign className="w-3 h-3" /> Sender email
            <span className="text-slate-600 ml-1">— must be on a Resend-verified domain</span>
          </label>
          <input
            type="email"
            value={senderInput}
            onChange={(e) => setSenderInput(e.target.value)}
            placeholder="no-reply@rishabsalgotra.com"
            className="w-full bg-slate-950/60 border border-slate-800 focus:border-[#39ff14]/60 rounded px-3 py-2 text-sm text-slate-200 font-mono outline-none"
            data-testid="input-sender-email"
          />
          <div className="flex items-center gap-2 mt-1.5 text-[10px] font-mono text-slate-500">
            <span>Effective: <span className="text-[#00f0ff]">{config?.sender_email}</span></span>
            {config?.source?.sender_from_db ? sourceBadge(true) : <span className="text-slate-600">(from env / default)</span>}
          </div>
        </div>

        <div>
          <label className="font-mono text-[10px] uppercase tracking-widest text-slate-400 flex items-center gap-1.5 mb-1.5">
            <KeyRound className="w-3 h-3" /> Resend API key
            <span className="text-slate-600 ml-1">— leave blank to keep the current key</span>
          </label>
          <input
            type="password"
            value={apiKeyInput}
            onChange={(e) => setApiKeyInput(e.target.value)}
            placeholder="re_xxxxxxxxxxxx (only if you want to change it)"
            className="w-full bg-slate-950/60 border border-slate-800 focus:border-[#39ff14]/60 rounded px-3 py-2 text-sm text-slate-200 font-mono outline-none"
            data-testid="input-resend-api-key"
            autoComplete="new-password"
          />
          <div className="flex items-center gap-2 mt-1.5 text-[10px] font-mono text-slate-500">
            <span>
              Current key: <span className="text-[#00f0ff]">{config?.resend_api_key_masked || 'NOT SET'}</span>
            </span>
            {config?.source?.api_key_from_db ? sourceBadge(true) : <span className="text-slate-600">(from env)</span>}
          </div>
        </div>

        <button
          onClick={save}
          disabled={saving}
          className="px-4 py-2 rounded font-mono text-[11px] uppercase tracking-widest font-bold flex items-center gap-2 bg-[#39ff14]/15 border border-[#39ff14]/40 text-[#39ff14] hover:bg-[#39ff14] hover:text-slate-950 disabled:opacity-50 transition-all cursor-pointer"
          data-testid="btn-save-email-settings"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          <span>Save settings</span>
        </button>
      </div>

      <div className="border-t border-slate-900 pt-4 space-y-3">
        <div className="font-mono text-[10px] uppercase tracking-widest text-slate-400 flex items-center gap-1.5">
          <ShieldCheck className="w-3 h-3 text-[#ff9e00]" /> Send test email
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="email"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            placeholder="recipient@example.com"
            className="flex-1 bg-slate-950/60 border border-slate-800 focus:border-[#ff9e00]/60 rounded px-3 py-2 text-sm text-slate-200 font-mono outline-none"
            data-testid="input-test-recipient"
          />
          <button
            onClick={sendTest}
            disabled={testing || !testTo}
            className="px-4 py-2 rounded font-mono text-[11px] uppercase tracking-widest font-bold flex items-center gap-2 bg-[#ff9e00]/15 border border-[#ff9e00]/40 text-[#ff9e00] hover:bg-[#ff9e00] hover:text-slate-950 disabled:opacity-50 transition-all cursor-pointer shrink-0"
            data-testid="btn-send-test-email"
          >
            {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            <span>Dispatch test</span>
          </button>
        </div>
        <div className="font-mono text-[10px] text-slate-500 leading-snug">
          Hits Resend with the current settings above. If Resend rejects (e.g., unverified domain), the exact error appears below this card so you can adjust and retry without touching env vars.
        </div>
      </div>
    </section>
  );
};
